import { useState } from 'react'
import { AlertTriangle, Lock, Unlock } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { usePeriodStore } from '@/stores/periodStore'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface UnlockPeriodDialogProps {
  open: boolean
  period: string | null
  onClose: () => void
}

/**
 * Dialog for unlocking a locked salary period
 * Requires mandatory reason for audit trail
 */
export function UnlockPeriodDialog({ open, period, onClose }: UnlockPeriodDialogProps) {
  const { currentUser } = useAuth()
  const { unlockPeriod, unlockingPeriod } = usePeriodStore()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Format period for display
  const formatPeriod = (period: string): string => {
    const date = new Date(period)
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'long'
    })
  }

  const handleUnlock = async () => {
    if (!currentUser || !period) return

    // Validate reason
    if (!reason.trim()) {
      setError('Du måste ange en anledning för upplåsning')
      return
    }

    try {
      setError(null)
      await unlockPeriod(currentUser, period, reason.trim())

      // Reset and close
      setReason('')
      onClose()
    } catch (err) {
      // Error is already shown via toast in store, but we can show it here too
      setError(err instanceof Error ? err.message : 'Det gick inte att låsa upp perioden')
    }
  }

  const handleClose = () => {
    if (!unlockingPeriod) {
      setReason('')
      setError(null)
      onClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Unlock className="h-5 w-5" />
            Lås upp period
          </DialogTitle>
          <DialogDescription>
            {period && `Lås upp ${formatPeriod(period)} för redigering`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning Alert */}
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Varning:</strong> När du låser upp perioden kommer befintliga
              löne-snapshots att markeras som raderade. Du måste låsa perioden igen
              efter dina ändringar för att skapa nya snapshots.
            </AlertDescription>
          </Alert>

          {/* Reason Input */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="flex items-center gap-1">
              Anledning för upplåsning
              <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reason"
              placeholder="Beskriv varför perioden behöver låsas upp (t.ex. 'Korrigera tips för förare X', 'Lägg till glömd bonus')"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={unlockingPeriod}
              rows={3}
              className="resize-none"
            />
            <p className="text-sm text-muted-foreground">
              Anledningen sparas i systemets granskningslogg för revisionsspår.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Info about what happens */}
          <div className="rounded-lg border p-3 space-y-2 text-sm">
            <p className="font-medium">Vad händer när du låser upp:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Befintliga löne-snapshots markeras som raderade</li>
              <li>Du kan redigera intäkter och ersättningar för perioden</li>
              <li>Upplåsningen loggas med din anledning i systemet</li>
              <li>Du måste låsa perioden igen för att skapa nya snapshots</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={unlockingPeriod}
          >
            Avbryt
          </Button>
          <Button
            variant="destructive"
            onClick={handleUnlock}
            disabled={unlockingPeriod || !reason.trim()}
          >
            {unlockingPeriod ? (
              <>
                <Lock className="mr-2 h-4 w-4 animate-spin" />
                Låser upp...
              </>
            ) : (
              <>
                <Unlock className="mr-2 h-4 w-4" />
                Lås upp period
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
